import { useCallback } from 'react';
import { useSession } from '@/context/SessionContext';
import { supabase } from '@/lib/supabase/client';
import { getTempCart } from '@/lib/tempCart';
import { getOrCreateCartForStore } from '@/lib/supabase/carts';

export const useSyncLocalCart = () => {
  const { session } = useSession();

  const undoSyncCart = useCallback(async (syncedItems: { id: string }[]) => {
    if (!session?.user?.id || syncedItems.length === 0) return;

    try {
      const idsToDelete = syncedItems.map(item => item.id);
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .in('id', idsToDelete);

      if (error) throw error;
    } catch (err) {
      console.error('[useSyncLocalCart] Error undoing sync:', err);
    }
  }, [session?.user?.id]);

  const syncLocalCartToDB = useCallback(async () => {
    if (!session?.user?.id) {
      return { success: false, message: 'Usuario no logueado' };
    }

    try {
      const tempCart = getTempCart();
      
      if (!tempCart || !tempCart.items || tempCart.items.length === 0) {
        return { success: false, message: 'No hay items en el canasto local' };
      }

      // Validate storeId before proceeding — prevents ghost cart creation
      const storeId = tempCart.store_id;
      if (!storeId || typeof storeId !== 'string' || storeId.trim() === '') {
        console.warn('[useSyncLocalCart] Temp cart has no valid store_id, aborting to prevent ghost cart');
        return { success: false, message: 'El canasto local no tiene una tienda válida' };
      }

      // Keep backup for undo
      const backupItems = [...tempCart.items];

      // Filter out items with invalid product_id
      const validItems = tempCart.items.filter(item => item.product_id && typeof item.product_id === 'string');
      if (validItems.length === 0) {
        return { success: false, message: 'No hay items válidos en el canasto local' };
      }

      // Add items to DB for this store
      let totalItemsAdded = 0;
      const syncedRecords: { id: string }[] = [];

      {
        const items = validItems;
        try {
          // Get or create cart for this store
          const cart = await getOrCreateCartForStore(session.user.id, storeId);

          // Add items to cart (upsert to handle duplicates - sum quantities)
          for (const item of items) {
            // Check if item already exists in this cart
            const { data: existing } = await supabase
              .from('cart_items')
              .select('id, quantity')
              .eq('cart_id', cart.id)
              .eq('product_id', item.product_id)
              .maybeSingle();

            let data: { id: string } | null = null;
            let error: unknown = null;

            if (existing) {
              // Update existing item's quantity
              const { data: updated, error: updateErr } = await supabase
                .from('cart_items')
                .update({ quantity: existing.quantity + item.quantity })
                .eq('id', existing.id)
                .select('id')
                .single();
              data = updated;
              error = updateErr;
            } else {
              // Insert new item
              const { data: inserted, error: insertErr } = await supabase
                .from('cart_items')
                .insert({
                  cart_id: cart.id,
                  product_id: item.product_id,
                  quantity: item.quantity,
                  unit_price: item.unit_price,
                })
                .select('id')
                .single();
              data = inserted;
              error = insertErr;
            }

            if (!error && data) {
              totalItemsAdded += item.quantity;
              syncedRecords.push(data);
            }
          }
        } catch (err) {
          console.error(`[useSyncLocalCart] Error syncing store ${storeId}:`, err);
        }
      }

      // Only clear temp cart if something was actually synced
      if (totalItemsAdded > 0) {
        localStorage.removeItem('temp-cart-minibid24hs');
      }

      return {
        success: totalItemsAdded > 0,
        message: `Se importaron ${totalItemsAdded} producto${totalItemsAdded !== 1 ? 's' : ''} al canasto`,
        count: totalItemsAdded,
        syncedRecords,
        backupItems,
        storeId: tempCart.store_id
      };
    } catch (err) {
      console.error('[useSyncLocalCart] Error:', err);
      return { success: false, message: 'Error al sincronizar canasto' };
    }
  }, [session?.user?.id]);

  return { syncLocalCartToDB, undoSyncCart };
};
